import { Component, Ref, Vue } from 'vue-property-decorator';
import DataTable, { TableVisibility } from '@/components/data-table/DataTable';
import { Options } from '@/components/mi-dialog/MiDialog';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import SupplierSizeDeviationCreateOrUpdateDialog from '@/components/CustomDialog/SupplierSizeDeviationCreateOrUpdateDialog/SupplierSizeDeviationCreateOrUpdateDialog.vue';
import { SupplierSizeDeviation } from '@/models/SupplierSizeDeviation';
import { Brand } from '@/models/Brand';

@Component<SettingsSupplierSizeDeviations>({
  components: {
    SupplierSizeDeviationCreateOrUpdateDialog,
  },
})
export default class SettingsSupplierSizeDeviations extends Vue {
  @Ref()
  protected supplierSizeDeviationsDatatable!: DataTable;

  protected selectedBrand: null | Brand = null;

  protected selectedSupplierSizeDeviation: null | SupplierSizeDeviation = null;

  protected isCreatingOrUpdatingSupplierSizeDeviation: boolean = false;

  protected supplierSizeDeviationsTableVisibility: TableVisibility = {
    title: false,
    checkboxes: false,
    search: true,
  }

  protected getBrandName(brand: undefined | null | string | Brand): string {
    if (! brand) { return ''; }

    if (typeof brand === 'string') { return brand; }

    return brand.name || '';
  }

  protected getSupplierSizeName(supplierSizeDeviaton: SupplierSizeDeviation): string {
    return supplierSizeDeviaton
      && supplierSizeDeviaton.supplier_size
      && supplierSizeDeviaton.supplier_size.name
        ? supplierSizeDeviaton.supplier_size.name
        : '-';
  }

  protected getMaisonSizeName(supplierSizeDeviaton: SupplierSizeDeviation): string {
    return supplierSizeDeviaton
      && supplierSizeDeviaton.size
      && supplierSizeDeviaton.size.name
        ? supplierSizeDeviaton.size.name
        : '-';
  }

  protected onIsCreatingSupplierSizeDeviationInput(): void {
    this.selectedSupplierSizeDeviation = null;
  }

  protected onSupplierSizeDeviationCreated(): void {
    this.refreshSupplierSizeDeviationsDatatable();
  }

  protected onSupplierSizeDeviationUpdated(): void {
    this.refreshSupplierSizeDeviationsDatatable();
  }

  protected refreshSupplierSizeDeviationsDatatable(): void {
    if (! this.supplierSizeDeviationsDatatable) { return; }

    this.supplierSizeDeviationsDatatable.refresh(true);
  }

  protected async deleteSupplierSizeDeviation(): Promise<null | SupplierSizeDeviation> {
    if (!this.selectedSupplierSizeDeviation) { return null; }

    const selectedSupplierSizeDeviation = await this.selectedSupplierSizeDeviation
      .delete()
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    return selectedSupplierSizeDeviation;
  }

  protected get selectedBrandId(): string {
    return this.selectedBrand
      ? this.selectedBrand.id || ''
      : '';
  }

  protected get deleteSupplierSizeDeviationDialogOptions(): Options {
    return {
      title: 'Confirm deletion',
      text: 'Are you sure you wish to delete this supplier brand specific size?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Delete',
          action: async () => {
            const selectedSupplierSizeDeviation = await this.deleteSupplierSizeDeviation();

            if (! selectedSupplierSizeDeviation) { return; }

            this.selectedSupplierSizeDeviation = null;

            this.refreshSupplierSizeDeviationsDatatable();
          },
        },
        cancel: {
          text: 'Cancel',
          action: () => (this.selectedSupplierSizeDeviation = null),
        },
      },
    };
  }

  protected get supplierSizeDeviationsTableOptions(): Object {
    return {
      model: new SupplierSizeDeviation()
        .filter({
          ...this.selectedBrandId
            ? {
              'brand': this.selectedBrandId,
            }
            : {}
        })
        .include([
          'brand',
          'size',
          'supplier_size',
        ]),
      name: {
        singular: 'Supplier size specific per brand',
        plural: 'Supplier size specific per brand',
      },
      sort: {
        key: 'name',
        order: 'ASC',
      },
      headers: [
        {
          text: 'Brand',
          value: 'brand',
          sortable: {
            key: 'brand',
            order: 'ASC',
          },
          transform: (brand: undefined | null | string | Brand) => this.getBrandName(brand),
        },
        {
          text: 'Supplier size',
          value: 'supplier_size__name',
          sortable: {
            key: 'supplier_size__name',
            order: 'ASC',
          },
          transform: (supplier_size__name: undefined, supplierSizeDeviaton: SupplierSizeDeviation) => this.getSupplierSizeName(supplierSizeDeviaton),
        },
        {
          text: 'Maison size',
          value: 'size__name',
          sortable: {
            key: 'size__name',
            order: 'ASC',
          },
          transform: (size__name: undefined, supplierSizeDeviaton: SupplierSizeDeviation) => this.getMaisonSizeName(supplierSizeDeviaton),
        },
      ],
      actions: [
        {
          type: 'edit',
          label: 'edit',
          icon: 'edit',
          action: (datatable: DataTable, supplierSizeDeviaton: SupplierSizeDeviation) => {
            this.selectedSupplierSizeDeviation = supplierSizeDeviaton;

            this.isCreatingOrUpdatingSupplierSizeDeviation = true;
          },
        },
        {
          type: 'delete',
          label: 'delete',
          icon: 'delete',
          action: (datatable: DataTable, supplierSizeDeviaton: SupplierSizeDeviation) => {
            this.selectedSupplierSizeDeviation = supplierSizeDeviaton;

            this.$store.dispatch('openDialog', this.deleteSupplierSizeDeviationDialogOptions);
          },
        },
      ],
      add: () => (this.isCreatingOrUpdatingSupplierSizeDeviation = true)
    };
  }
}
